// Type-only import — no runtime edge from `schemas/` into `pricing/`, so the
// two stay acyclic while the category-markup map below is exhaustiveness-
// checked against the pricing module's content-category vocabulary.
import type { PricingLegacyCategoryKey } from '../pricing/content-categories';
import { z } from 'zod';

// Legacy enum: /src/settings/enum/default-pricing.enum.ts. The legacy
// `DefaultPricingProperty` enum's *values* are these lowercase tokens
// (`GUIDE_PRICE_HD = 'guide_price_hd'`), which is exactly what the
// `organizations.default_pricing.property` column stores and what the rest
// of the system (legacy backend, pricing engine) reads. Keep the wire/DB
// value lowercase — do NOT uppercase it, or `findAll` rows fail output
// validation and the procedure 500s (empty table despite existing rows).
// Display order is authored in service-layer sort, not in this enum — see
// PROPERTY_ORDER in apps/auth/api/src/default-pricing/default-pricing.service.ts.
//
// The first six are the LEGACY subitem properties (V2 wrote and still reads
// exactly these). The `category_markup_*` block is V3-only: one per content
// category, applied to a top-level itinerary item when it is added to an
// itinerary — see `apps/auth/prd/default-category-markup.md`. The
// `category_markup_` prefix is deliberate: `transport_markup` (the Transport
// SUBITEM markup) and `category_markup_transportation` (the Transportation
// CATEGORY markup) are different settings, and an unprefixed
// `transportation_markup` would read as a typo of the former.
export const defaultPricingProperty = z.enum([
  'transport_markup',
  'ticket_markup',
  'guide_markup_hd',
  'guide_markup_fd',
  'guide_price_hd',
  'guide_price_fd',
  // XT-828: the Transport a Mini Planner adds for a day's experiences.
  'transport_price',
  'category_markup_accommodation',
  'category_markup_activity',
  'category_markup_attraction',
  'category_markup_destination',
  'category_markup_dining',
  'category_markup_information',
  'category_markup_other',
  'category_markup_personnel',
  'category_markup_transportation',
  'category_markup_venue',
]);

export type DefaultPricingProperty = z.infer<typeof defaultPricingProperty>;

// Content category (legacy key, as resolved by `resolvePricingLegacyCategoryKey`
// in @travel-planner/shared/pricing) → its default-markup property. Total over
// the pricing module's category keys, so a new content category fails to
// compile here until it gets a property token.
export const CATEGORY_MARKUP_PROPERTY_BY_CATEGORY = {
  ACCOMMODATION: 'category_markup_accommodation',
  ACTIVITY: 'category_markup_activity',
  ATTRACTION: 'category_markup_attraction',
  DESTINATION: 'category_markup_destination',
  DINING: 'category_markup_dining',
  INFORMATION: 'category_markup_information',
  OTHER: 'category_markup_other',
  PERSONNEL: 'category_markup_personnel',
  TRANSPORTATION: 'category_markup_transportation',
  VENUE: 'category_markup_venue',
} as const satisfies Record<PricingLegacyCategoryKey, DefaultPricingProperty>;

export const CATEGORY_MARKUP_PROPERTIES: ReadonlyArray<DefaultPricingProperty> =
  Object.values(CATEGORY_MARKUP_PROPERTY_BY_CATEGORY);

const categoryMarkupPropertySet: ReadonlySet<string> = new Set(
  CATEGORY_MARKUP_PROPERTIES
);

// A category markup is a percentage by construction — "Local Currency" has no
// meaning for it (there is no per-category flat fee). Enforced on the write
// paths below and, for partial patches, in the default-pricing service.
export const isCategoryMarkupProperty = (
  property: string | null | undefined
): property is DefaultPricingProperty =>
  property != null && categoryMarkupPropertySet.has(property);

// Legacy stored these exact string values: 'Local Currency' and 'Percentage'.
export const defaultPricingType = z.enum(['Local Currency', 'Percentage']);

export type DefaultPricingType = z.infer<typeof defaultPricingType>;

export const countryValueSchema = z.object({
  country: z.string().min(2).max(3),
  value: z.number().refine((v) => Number.isFinite(v), {
    message: 'value must be a finite number',
  }),
});

export type CountryValue = z.infer<typeof countryValueSchema>;

export const defaultPricingItemSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  property: defaultPricingProperty,
  type: defaultPricingType,
  default: z.string(),
  countryValues: z.array(countryValueSchema),
  isActive: z.boolean(),
  createdOn: z.string(),
  modifiedOn: z.string().nullable(),
});

export type DefaultPricingItem = z.infer<typeof defaultPricingItemSchema>;

const numericInput = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? v.toString() : v))
  .refine((v) => /^-?\d+(\.\d+)?$/.test(v), {
    message: 'default must be a numeric value',
  });

// Shared message so the API, the form and the tests all name the rule once.
export const CATEGORY_MARKUP_TYPE_MESSAGE =
  'A category markup must use the Percentage type';

export const createDefaultPricingInput = z
  .object({
    title: z.string().min(1),
    property: defaultPricingProperty,
    type: defaultPricingType,
    default: numericInput,
    countryValues: z.array(countryValueSchema).default([]),
    isActive: z.boolean().optional(),
  })
  .refine(
    (v) => !isCategoryMarkupProperty(v.property) || v.type === 'Percentage',
    { message: CATEGORY_MARKUP_TYPE_MESSAGE, path: ['type'] }
  );

export type CreateDefaultPricingInput = z.infer<
  typeof createDefaultPricingInput
>;

export const updateDefaultPricingInput = z
  .object({
    id: z.uuid(),
    title: z.string().min(1).optional(),
    property: defaultPricingProperty.optional(),
    type: defaultPricingType.optional(),
    default: numericInput.optional(),
    countryValues: z.array(countryValueSchema).optional(),
    isActive: z.boolean().optional(),
  })
  .refine(
    (v) =>
      v.title !== undefined ||
      v.property !== undefined ||
      v.type !== undefined ||
      v.default !== undefined ||
      v.countryValues !== undefined ||
      v.isActive !== undefined,
    { message: 'At least one field must be provided' }
  )
  // Only catches the case where BOTH halves are in the patch. A patch that
  // supplies just `type` is validated against the STORED property by the
  // service (it loads the row anyway) — see `assertCategoryMarkupType`.
  .refine(
    (v) =>
      v.property === undefined ||
      v.type === undefined ||
      !isCategoryMarkupProperty(v.property) ||
      v.type === 'Percentage',
    { message: CATEGORY_MARKUP_TYPE_MESSAGE, path: ['type'] }
  );

export type UpdateDefaultPricingInput = z.infer<
  typeof updateDefaultPricingInput
>;

export const findDefaultPricingResultSchema = z.object({
  data: z.array(defaultPricingItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});

export type FindDefaultPricingResult = z.infer<
  typeof findDefaultPricingResultSchema
>;

export const deleteDefaultPricingInput = z.object({
  id: z.uuid(),
});

export type DeleteDefaultPricingInput = z.infer<
  typeof deleteDefaultPricingInput
>;

export const deleteDefaultPricingResultSchema = z.object({
  id: z.uuid(),
});

export type DeleteDefaultPricingResult = z.infer<
  typeof deleteDefaultPricingResultSchema
>;
